/**
 * RefugeEncartEditors — outils d'édition inline (admin) de la page Refuge.
 *
 * Permet d'AJOUTER et de SUPPRIMER des encarts sur /refuge :
 *   - Grands espaces "Chaque espace" (image + texte, format simple ou galerie)
 *   - Lignes "Bon à savoir" (icône + texte)
 *   - Cartes "L'essentiel · Ce qui vous attend" (icône + label)
 *
 * Les composants ne rendent rien pour les visiteurs non connectés.
 */

import { useEffect, useState } from "react";
import {
  Plus, X, Trash2, RotateCcw, Waves, UtensilsCrossed, BedDouble, Leaf,
  Sun, Volume2, Home, Thermometer, Compass, Sparkles, Star, Coffee,
  Wifi, Flower2, TreePalm, Bath, Wind, Moon, Heart, Camera,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import MediaPicker from "./MediaPicker";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ICON_MAP = {
  Waves, UtensilsCrossed, BedDouble, Leaf, Sun, Volume2, Home, Thermometer,
  Compass, Sparkles, Star, Coffee, Wifi, Flower2, TreePalm, Bath, Wind, Moon,
  Heart, Camera,
};
export const ICON_NAMES = Object.keys(ICON_MAP);

function resolveMedia(src) {
  if (!src) return "";
  if (src.startsWith("http") || src.startsWith("/")) return src;
  return `${BACKEND}/api/media/${encodeURIComponent(src)}`;
}

/* ---------- Bouton "Supprimer cet encart" (flottant, admin) ---------- */
export function RemoveEncartBtn({ onRemove, className = "", dataTestid }) {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <button
      type="button"
      onClick={() => {
        if (window.confirm("Supprimer cet encart de la page ?")) onRemove();
      }}
      data-testid={dataTestid}
      className={`absolute z-30 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 text-white text-[10px] font-body uppercase tracking-[0.15em] shadow-lg hover:bg-refuge-ruby transition-all backdrop-blur-sm ${className}`}
      title="Supprimer cet encart"
    >
      <Trash2 size={12} strokeWidth={1.5} />
      Supprimer
    </button>
  );
}

/* ---------- Bouton "Ajouter un encart" (admin) ---------- */
export function AddEncartBar({ label, onClick, dataTestid }) {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <div className="flex justify-center mt-10">
      <button
        type="button"
        onClick={onClick}
        data-testid={dataTestid}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-dashed border-refuge-ruby/50 text-refuge-ruby text-xs font-body uppercase tracking-[0.15em] hover:bg-refuge-ruby hover:text-white transition-all"
      >
        <Plus size={14} strokeWidth={1.5} />
        {label}
      </button>
    </div>
  );
}

/* ---------- Bandeau "restaurer les encarts masqués" (admin) ---------- */
export function RestoreHiddenBar({ hidden = [], labelFor, onRestore, dataTestid }) {
  const { user } = useAuth();
  if (!user || !hidden.length) return null;
  return (
    <div
      data-testid={dataTestid}
      className="max-w-3xl mx-auto mt-6 flex flex-wrap items-center justify-center gap-2 text-[11px] font-body text-refuge-soft"
    >
      <span className="italic">Encarts masqués :</span>
      {hidden.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => onRestore(id)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-refuge-border bg-white hover:border-refuge-ruby hover:text-refuge-ruby transition-all"
          title="Restaurer"
        >
          <RotateCcw size={11} />
          {labelFor ? labelFor(id) : id}
        </button>
      ))}
    </div>
  );
}

/* ---------- Sélecteur d'icône ---------- */
function IconPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-8 gap-2">
      {ICON_NAMES.map((name) => {
        const Ico = ICON_MAP[name];
        const active = value === name;
        return (
          <button
            key={name}
            type="button"
            onClick={() => onChange(name)}
            className={`aspect-square rounded-lg border flex items-center justify-center transition-all ${
              active
                ? "border-refuge-ruby bg-refuge-ruby/10 text-refuge-ruby"
                : "border-refuge-border text-refuge-soft hover:border-refuge-ruby/50"
            }`}
            title={name}
            data-testid={`icon-opt-${name}`}
          >
            <Ico size={18} strokeWidth={1.2} />
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Champ image (ouvre MediaPicker) ---------- */
function ImageField({ label, value, onChange, dataTestid }) {
  const [open, setOpen] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  useEffect(() => {
    if (open) api.listMedia("refuge").then(setUploaded).catch(() => {});
  }, [open]);
  return (
    <div>
      <label className="block font-body text-xs uppercase tracking-[0.15em] text-refuge-soft mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <div className="w-20 h-16 rounded-lg overflow-hidden bg-refuge-bg border border-refuge-border flex items-center justify-center shrink-0">
          {value ? (
            <img src={resolveMedia(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <Camera size={16} className="text-refuge-soft" />
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={dataTestid}
          className="px-4 py-2 rounded-full bg-refuge-text text-white text-xs font-body hover:bg-refuge-ruby transition-all"
        >
          {value ? "Changer l'image" : "Choisir une image"}
        </button>
      </div>
      {open && (
        <MediaPicker
          uploaded={uploaded}
          onPick={(fn) => { onChange(fn); setOpen(false); }}
          onClose={() => setOpen(false)}
          onUploaded={async () => setUploaded(await api.listMedia("refuge"))}
          site="refuge"
          accept="image/*"
        />
      )}
    </div>
  );
}

/* ---------- Modal : ajouter un GRAND ESPACE ---------- */
export function AddSpaceModal({ onClose, onSave }) {
  const [layout, setLayout] = useState("simple"); // "simple" | "gallery"
  const [reversed, setReversed] = useState(false);
  const [eyebrow, setEyebrow] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [text, setText] = useState("");
  const [bullets, setBullets] = useState("");
  const [mainImage, setMainImage] = useState("");
  const [gallery, setGallery] = useState([{ src: "", alt: "" }, { src: "", alt: "" }]);

  const canSave = title.trim() && mainImage;

  const submit = () => {
    if (!canSave) return;
    onSave({
      id: `custom-${Date.now()}`,
      layout,
      reversed,
      eyebrow: eyebrow.trim(),
      title: title.trim(),
      subtitle: subtitle.trim(),
      text: text.trim(),
      bullets: bullets.split("\n").map((b) => b.trim()).filter(Boolean),
      mainImage,
      gallery: layout === "gallery" ? gallery.filter((g) => g.src) : [],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl overflow-hidden flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-space-modal"
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-refuge-border shrink-0">
          <p className="font-heading text-lg font-light text-refuge-text">Ajouter un espace</p>
          <button type="button" onClick={onClose} className="p-1.5 text-refuge-soft hover:text-refuge-ruby">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          {/* Format */}
          <div>
            <label className="block font-body text-xs uppercase tracking-[0.15em] text-refuge-soft mb-2">Format</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setLayout("simple")}
                data-testid="space-layout-simple"
                className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-body transition-all ${layout === "simple" ? "border-refuge-ruby bg-refuge-ruby/10 text-refuge-ruby" : "border-refuge-border text-refuge-soft"}`}
              >
                Image simple + texte
              </button>
              <button
                type="button"
                onClick={() => setLayout("gallery")}
                data-testid="space-layout-gallery"
                className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-body transition-all ${layout === "gallery" ? "border-refuge-ruby bg-refuge-ruby/10 text-refuge-ruby" : "border-refuge-border text-refuge-soft"}`}
              >
                Image + 2 photos
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm font-body text-refuge-text">
            <input type="checkbox" checked={reversed} onChange={(e) => setReversed(e.target.checked)} data-testid="space-reversed" />
            Image à droite (inverser le sens)
          </label>

          <Field label="Surtitre (petit texte rouge)" value={eyebrow} onChange={setEyebrow} placeholder="Le bassin minéral" testid="space-eyebrow" />
          <Field label="Titre" value={title} onChange={setTitle} placeholder="Le bassin minéral" testid="space-title" />
          <Field label="Sous-titre (en italique)" value={subtitle} onChange={setSubtitle} placeholder="Chauffé par le soleil" testid="space-subtitle" />

          <div>
            <label className="block font-body text-xs uppercase tracking-[0.15em] text-refuge-soft mb-2">Paragraphe</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="Description de l'espace…"
              className="w-full px-3 py-2 border border-refuge-border rounded-lg text-sm font-body"
              data-testid="space-text"
            />
          </div>

          <div>
            <label className="block font-body text-xs uppercase tracking-[0.15em] text-refuge-soft mb-2">Liste à puces (une ligne = une puce, optionnel)</label>
            <textarea
              value={bullets}
              onChange={(e) => setBullets(e.target.value)}
              rows={3}
              placeholder={"Terrasse couverte\nCoin de vie lumineux"}
              className="w-full px-3 py-2 border border-refuge-border rounded-lg text-sm font-body"
              data-testid="space-bullets"
            />
          </div>

          <ImageField label="Image principale" value={mainImage} onChange={setMainImage} dataTestid="space-main-image" />

          {layout === "gallery" && (
            <div className="space-y-3 border-t border-refuge-border/60 pt-4">
              <p className="font-body text-xs uppercase tracking-[0.15em] text-refuge-soft">2 petites photos</p>
              {gallery.map((g, i) => (
                <div key={i} className="flex items-end gap-3">
                  <ImageField
                    label={`Photo ${i + 1}`}
                    value={g.src}
                    onChange={(fn) => setGallery(gallery.map((x, j) => (j === i ? { ...x, src: fn } : x)))}
                    dataTestid={`space-gallery-image-${i}`}
                  />
                  <input
                    type="text"
                    value={g.alt}
                    onChange={(e) => setGallery(gallery.map((x, j) => (j === i ? { ...x, alt: e.target.value } : x)))}
                    placeholder="Légende"
                    className="flex-1 px-3 py-2 border border-refuge-border rounded-lg text-sm font-body"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-refuge-border shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-body text-refuge-soft hover:text-refuge-text">
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSave}
            data-testid="space-save-btn"
            className="px-5 py-2 rounded-full bg-refuge-ruby text-white text-sm font-body disabled:opacity-40 hover:bg-refuge-ruby/90 transition-all"
          >
            Ajouter l'espace
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Modal : ajouter une carte "essentiel" ou une ligne "bon à savoir" ---------- */
export function AddSimpleModal({ type, onClose, onSave }) {
  // type: "essentiel" (label + icône) | "bonasavoir" (texte + icône)
  const isEssentiel = type === "essentiel";
  const [txt, setTxt] = useState("");
  const [icon, setIcon] = useState(isEssentiel ? "Sparkles" : "Leaf");
  const canSave = txt.trim();

  const submit = () => {
    if (!canSave) return;
    if (isEssentiel) onSave({ id: `custom-${Date.now()}`, label: txt.trim(), icon });
    else onSave({ id: `custom-${Date.now()}`, text: txt.trim(), icon });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white rounded-2xl overflow-hidden flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-simple-modal"
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-refuge-border">
          <p className="font-heading text-lg font-light text-refuge-text">
            {isEssentiel ? "Ajouter une carte" : "Ajouter une info pratique"}
          </p>
          <button type="button" onClick={onClose} className="p-1.5 text-refuge-soft hover:text-refuge-ruby">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <label className="block font-body text-xs uppercase tracking-[0.15em] text-refuge-soft mb-2">
              {isEssentiel ? "Libellé" : "Texte de l'info"}
            </label>
            {isEssentiel ? (
              <input
                type="text"
                value={txt}
                onChange={(e) => setTxt(e.target.value)}
                placeholder="Bain nordique"
                className="w-full px-3 py-2 border border-refuge-border rounded-lg text-sm font-body"
                data-testid="simple-text"
              />
            ) : (
              <textarea
                value={txt}
                onChange={(e) => setTxt(e.target.value)}
                rows={3}
                placeholder="Le petit-déjeuner est servi entre 8h et 10h…"
                className="w-full px-3 py-2 border border-refuge-border rounded-lg text-sm font-body"
                data-testid="simple-text"
              />
            )}
          </div>
          <div>
            <label className="block font-body text-xs uppercase tracking-[0.15em] text-refuge-soft mb-2">Icône</label>
            <IconPicker value={icon} onChange={setIcon} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-refuge-border">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-body text-refuge-soft hover:text-refuge-text">
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSave}
            data-testid="simple-save-btn"
            className="px-5 py-2 rounded-full bg-refuge-ruby text-white text-sm font-body disabled:opacity-40 hover:bg-refuge-ruby/90 transition-all"
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- petit champ texte réutilisable ---------- */
function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block font-body text-xs uppercase tracking-[0.15em] text-refuge-soft mb-2">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-refuge-border rounded-lg text-sm font-body"
        data-testid={testid}
      />
    </div>
  );
}

export { resolveMedia };
